import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CashierField from '@/components/CashierField';
import api from '@/lib/api';
import { toast } from 'sonner';
import { todayISO } from '@/lib/format';
import { Loader2 } from 'lucide-react';

const DEFAULT = {
  date: todayISO(),
  termin: 'Termin 1',
  total: '',
  payment_method: 'Transfer',
  reference_number: '',
  note: '',
  paid_by: '',
  cashier: 'Agi',
  cashier_other: '',
};

export default function IncomeDialog({ open, onOpenChange, pid, initial, onSaved }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          date: (initial.date || todayISO()).substring(0, 10),
          termin: initial.termin || 'Termin 1',
          total: initial.total || '',
          payment_method: initial.payment_method || 'Transfer',
          reference_number: initial.reference_number || '',
          note: initial.note || '',
          paid_by: initial.paid_by || '',
          cashier: initial.cashier || 'Agi',
          cashier_other: initial.cashier_other || '',
        });
      } else setForm(DEFAULT);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    const total = parseFloat(form.total);
    if (!total || total <= 0) { toast.error('Nominal harus lebih dari 0'); return; }
    setSaving(true);
    try {
      const payload = {
        type: 'income',
        date: form.date,
        termin: form.termin,
        total,
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        note: form.note,
        paid_by: form.paid_by,
        cashier: form.cashier || null,
        cashier_other: form.cashier === 'Lainnya' ? (form.cashier_other || null) : null,
      };
      if (isEdit) await api.put(`/transactions/${initial.id}`, payload);
      else await api.post(`/projects/${pid}/transactions`, payload);
      toast.success(isEdit ? 'Pemasukan diperbarui' : 'Pemasukan tersimpan');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="income-dialog" aria-describedby="income-desc" className="max-w-lg bg-[var(--fpccs-surface-1)] border-[var(--fpccs-border)]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Pemasukan' : 'Tambah Pemasukan'}</DialogTitle>
          <p id="income-desc" className="sr-only">Form untuk mencatat pemasukan project.</p>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Tanggal *</Label>
            <Input type="date" required data-testid="income-date" value={form.date} onChange={(e) => set('date', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Termin</Label>
            <Input data-testid="income-termin" value={form.termin} onChange={(e) => set('termin', e.target.value)} placeholder="Termin 1 / DP / Pelunasan" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Nominal (Rp) *</Label>
            <Input type="number" min="0" required autoFocus data-testid="income-total" value={form.total} onChange={(e) => set('total', e.target.value)} placeholder="1000000" />
          </div>
          <div className="sm:col-span-2">
            <CashierField
              label="Uang Diterima Oleh (Kasir)"
              testid="income-cashier"
              required
              value={form.cashier}
              otherValue={form.cashier_other}
              onChange={(v) => set('cashier', v)}
              onOtherChange={(v) => set('cashier_other', v)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Metode Pembayaran</Label>
            <Select value={form.payment_method} onValueChange={(v) => set('payment_method', v)}>
              <SelectTrigger data-testid="income-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="Transfer">Transfer</SelectItem>
                <SelectItem value="QRIS">QRIS</SelectItem>
                <SelectItem value="Kartu Debit">Kartu Debit</SelectItem>
                <SelectItem value="Kartu Kredit">Kartu Kredit</SelectItem>
                <SelectItem value="Lainnya">Lainnya</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-fpccs">Nomor Referensi</Label>
            <Input data-testid="income-reference" value={form.reference_number} onChange={(e) => set('reference_number', e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Dari</Label>
            <Input data-testid="income-from" value={form.paid_by} onChange={(e) => set('paid_by', e.target.value)} placeholder="Nama client / pemberi dana" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-muted-fpccs">Catatan</Label>
            <Textarea rows={2} data-testid="income-note" value={form.note} onChange={(e) => set('note', e.target.value)} />
          </div>
          <DialogFooter className="sm:col-span-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-income" className="bg-[var(--fpccs-primary)] hover:bg-[var(--fpccs-primary-2)] text-[var(--fpccs-bg)]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? 'Simpan' : 'Tambah')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
